import { LogLevel } from '@environment/enums/log-level.enum';
import { captureSentryMessage, originalCaptureSentryMessage } from '@shared/sentry';

export const LOG_STORAGE_PREFIX = 'stored-logs';
export const LOG_STORAGE_ALARM_KEY = 'processDelayedLogs';
// Maximum number of logs to keep
const MAX_STORED_LOGS = 100;

function getTimestampFromKey(key: string): number {
  const timestamp = parseInt(key.split('::').pop() || '0');
  return isNaN(timestamp) ? 0 : timestamp;
}

export async function getDelayedStoredLogs(): Promise<string[]> {
  try {
    const items = await chrome.storage.local.get();
    return Object.keys(items)
      .filter(key => key.startsWith(LOG_STORAGE_PREFIX))
      .sort((a, b) => getTimestampFromKey(a) - getTimestampFromKey(b));
  } catch (e) {
    console.error('Error while getting delayed stored logs', e);
    return [];
  }
}

/**
 * Process delayed stored logs and send them to Sentry
 * Note: Use originalCaptureSentryMessage inside this function to avoid infinite loop
 */
export async function processDelayedStoredLogs(): Promise<void> {
  const delayedStoredLogs = await getDelayedStoredLogs();
  if (!navigator.onLine) {
    captureSentryMessage(`Network is offline, skipping processing ${delayedStoredLogs.length} delayed stored logs`);
    return;
  }
  const currentStorageInfo = await getStorageUsage();
  originalCaptureSentryMessage(
    `processDelayedStoredLogs : ${delayedStoredLogs.length} logs found`,
    {
      bytesInUse: currentStorageInfo.bytesInUse,
      itemCount: currentStorageInfo.itemCount,
    },
    LogLevel.Debug
  );
  if (delayedStoredLogs.length === 0) {
    return;
  }

  // If we have more logs than MAX_STORED_LOGS, remove the oldest ones
  if (delayedStoredLogs.length > MAX_STORED_LOGS) {
    const logsToRemove = delayedStoredLogs.slice(0, delayedStoredLogs.length - MAX_STORED_LOGS);
    console.log(`Rotating out ${logsToRemove.length} oldest logs due to storage limit`);
    for (const key of logsToRemove) {
      await chrome.storage.local.remove(key);
    }
  }

  console.log(`Found ${delayedStoredLogs.length} delayed stored logs, processing...`);
  const batchId = Date.now();
  for (const key of delayedStoredLogs.slice(-MAX_STORED_LOGS)) {
    const storedItem = await chrome.storage.local.get(key);
    const log = storedItem[key];
    const timestamp = getTimestampFromKey(key);
    const progress = `${delayedStoredLogs.indexOf(key) + 1}/${delayedStoredLogs.length}`;

    try {
      // Add extra metadata to the log
      originalCaptureSentryMessage(
        `DelayedLog (${progress}) : ${log.message}`,
        {
          ...log.data,
          metadata: {
            delayMs: Date.now() - timestamp,
            originalTimestamp: timestamp,
            processedAt: Date.now(),
          },
        },
        log.level,
        { ...log.tags, batchId }
      );
    } catch (e) {
      console.error('Failed to send stored log:', e);
    } finally {
      await chrome.storage.local.remove(key);
    }
  }

  async function getStorageUsage(): Promise<{ bytesInUse: number; itemCount: number }> {
    return new Promise(resolve => {
      chrome.storage.local.getBytesInUse(null, bytesInUse => {
        chrome.storage.local.get(null, items => {
          resolve({
            bytesInUse,
            itemCount: Object.keys(items).length,
          });
        });
      });
    });
  }
}
